import { useMemo, useState } from 'react';

import {
    Combobox,
    ComboboxProvider,
    useComboboxStore,
    SelectProvider as AriakitSelectProvider,
    Select as AriakitSelect,
    SelectPopover as AriakitSelectPopover,
    SelectItem as AriakitSelectItem,
    useSelectContext,
} from '@ariakit/react';
import { matchSorter } from 'match-sorter';

import { colors } from '../../colors';
import { CaretCircleDownIcon, CircularCloseIcon } from '../icons';
import { Input } from '../Input/Input';
import { SelectBox } from '../SelectBox/SelectBox';

import * as css from './Select.css';

export type SelectOption = {
    /**
     * Unique option value.
     * This is the value returned to `onChange`.
     */
    value: string;

    /**
     * Visible label rendered for the option.
     *
     * Note: if `searchBox` is enabled and `label` is not a string,
     * search matching may be unreliable. Prefer string labels or provide
     * a dedicated searchable field.
     */
    label: string | React.ReactNode;

    /**
     * Plain-text value used when filtering searchable options.
     * Useful when `label` contains non-text UI such as icons.
     */
    searchLabel?: string;
};

export type SelectProps = {
    /**
     * List of options displayed in the popover.
     */
    options: SelectOption[];

    /**
     * Selected values (multi-select mode).
     * Used when `multiple` is true.
     */
    selectedValues?: string[];

    /**
     * Selected value (single-select mode).
     * Used when `multiple` is false.
     */
    selectedValue?: string | undefined;

    /**
     * Called when selection changes.
     * Always returns an array of values. In single-select mode, the array contains
     * either one value or is empty.
     */
    onChange: (selectedValues: string[]) => void;

    /**
     * Placeholder displayed when no option is selected.
     *
     * @default 'Select'
     */
    placeholder?: string;

    /**
     * Label shown at the top of the popover (close header).
     *
     * @default 'Select'
     */
    label?: string;

    /**
     * Width of the select trigger/popover container.
     *
     * @default 200
     */
    width?: number | string;

    /**
     * Disables interaction.
     *
     * @default false
     */
    disabled?: boolean;

    /**
     * Enables a search input in the popover to filter options.
     *
     * @default false
     */
    searchBox?: boolean;

    /**
     * Controls whether the first matching option is automatically highlighted.
     *
     * @default 'always'
     */
    autoSelect?: boolean | 'always';

    /**
     * Enables multiple selection.
     *
     * @default false
     */
    multiple?: boolean;

    /**
     * Applies the secondary visual theme.
     *
     * @default false
     */
    secondary?: boolean;
};

const ClosePopoverLabel = ({ label, secondary }: Pick<SelectProps, 'label' | 'secondary'>) => {
    const select = useSelectContext();
    return (
        <div className={css.label} onClick={() => select?.hide()}>
            <span className={css.labelText({ secondary })}>{label}</span>
            <CircularCloseIcon color={secondary ? colors.teal[700] : colors.bluegray[600]} />
        </div>
    );
};

export const Select = ({
    options,
    selectedValues: selected,
    selectedValue,
    onChange,
    placeholder = 'Select',
    label = 'Select',
    width = 200,
    disabled = false,
    searchBox = false,
    autoSelect = 'always',
    multiple = false,
    secondary = false,
}: SelectProps) => {
    const [search, setSearch] = useState('');
    const combobox = useComboboxStore({ value: search, setValue: setSearch });

    const selectedValues = useMemo(() => {
        if (multiple) return selected ?? [];
        return selectedValue ? [selectedValue] : [];
    }, [selected, selectedValue, multiple]);

    const matches = useMemo(() => {
        if (!searchBox) return options;
        if (!search) return options;
        const keys = ['searchLabel', 'label', 'value'] as const;
        return matchSorter(options, search, { keys });
    }, [options, search, searchBox]);

    const selectedOptions = useMemo(() => {
        if (!options || selectedValues.length === 0) return [];
        const set = new Set(selectedValues);
        return options.filter((opt) => set.has(opt.value));
    }, [options, selectedValues]);

    const displayText = useMemo(() => {
        if (selectedOptions.length === 0) return placeholder;
        if (selectedOptions.length === 1) return selectedOptions[0].label;
        return `${selectedOptions.length} selected`;
    }, [placeholder, selectedOptions]);

    return (
        <AriakitSelectProvider value={selectedValues} setValue={multiple ? onChange : undefined} combobox={combobox}>
            <div className={css.container} style={{ width }}>
                <AriakitSelect className={css.trigger({ secondary })} aria-disabled={disabled} disabled={disabled}>
                    <span className={css.displayText({ secondary })}>{displayText}</span>
                    <CaretCircleDownIcon
                        size={24}
                        color={secondary ? colors.teal[700] : colors.blue[500]}
                        weight="fill"
                    />
                </AriakitSelect>
                <AriakitSelectPopover portal className={css.popover({ secondary })} role="listbox" gutter={4} sameWidth>
                    <ClosePopoverLabel label={label} secondary={secondary} />
                    {searchBox && (
                        <ComboboxProvider store={combobox}>
                            <div className={css.searchInput}>
                                <Combobox
                                    autoSelect={autoSelect}
                                    autoFocus
                                    aria-label={`Search`}
                                    render={(comboboxProps) => {
                                        const { className: _ignoredClassName, ...rest } = comboboxProps;
                                        return (
                                            <Input
                                                {...rest}
                                                theme={secondary ? 'secondary' : undefined}
                                                placeholder={`Search...`}
                                                size="small"
                                            />
                                        );
                                    }}
                                />
                            </div>
                        </ComboboxProvider>
                    )}
                    <div className={css.options}>
                        {search && matches.length === 0 ? (
                            <div className={css.empty}>{`No results for "${search}"`}</div>
                        ) : (
                            matches.map((opt) => {
                                const isSelected = selectedValues.includes(opt.value);
                                return (
                                    <AriakitSelectItem
                                        key={opt.value}
                                        value={opt.value}
                                        hideOnClick={!multiple}
                                        className={css.item({ multiple })}
                                        onClick={
                                            multiple
                                                ? undefined
                                                : () => {
                                                      onChange([opt.value]);
                                                  }
                                        }
                                    >
                                        {multiple && <SelectBox selected={isSelected} color="gray" />}
                                        <span className={css.optionLabel({ isSelected, secondary })}>{opt.label}</span>
                                    </AriakitSelectItem>
                                );
                            })
                        )}
                    </div>
                </AriakitSelectPopover>
            </div>
        </AriakitSelectProvider>
    );
};
